import React, { useState } from 'react';
import { ExternalLink, Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { FieldGrid } from '../../components/FieldControl.jsx';
import { isWebUrl } from '../../lib/url.js';

const CATEGORIES = ['Collaboration', 'Commercial', 'Delivery', 'Support'];

const FIELDS = [
  { key: 'category', label: 'Category', type: 'select', options: CATEGORIES },
  { key: 'title', label: 'Title' },
  { key: 'resourceType', label: 'Type' },
  { key: 'owner', label: 'Owner' },
  { key: 'lastReviewed', label: 'Last reviewed', type: 'date' },
  { key: 'url', label: 'URL', full: true },
  { key: 'description', label: 'Description', type: 'textarea', full: true },
];

// Resources grouped by category (Collaboration / Commercial / Delivery /
// Support), with a compact "Key documents" strip of the starred ones — the
// prototype's layout. Add/edit/delete/star all save the whole list back
// through `onReplace`.
export function ProjectResources({ resources, onReplace, toForm, fromForm }) {
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const payload = (r) => fromForm(toForm(r));

  async function commit(next) {
    setBusy(true);
    setError('');
    try {
      await onReplace(next.filter((item) => item.title));
      setAdding(false);
      setEditingId(null);
      setConfirmId(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  // A blank title would be filtered out by `commit`, silently deleting the row
  // instead of the delete/Remove flow — report it instead.
  const titled = (form) => Boolean(fromForm(form).title);

  const add = (form) => {
    if (!titled(form)) return setError('A resource needs a title.');
    return commit([...resources.map(payload), fromForm(form)]);
  };
  const update = (id, form) => {
    if (!titled(form)) return setError('A resource needs a title.');
    return commit(
      resources.map((r) => (r.id === id ? fromForm(form) : payload(r))),
    );
  };
  const remove = (id) =>
    commit(resources.filter((r) => r.id !== id).map(payload));
  const toggleKey = (item) =>
    commit(
      resources.map((r) =>
        r.id === item.id
          ? { ...payload(r), keyDocument: !r.keyDocument }
          : payload(r),
      ),
    );

  const keyDocs = resources.filter((r) => r.keyDocument);

  return (
    <section className="card project-resources">
      <div className="editable-list-head">
        <h2>Resources</h2>
        <button
          type="button"
          className="secondary small"
          onClick={() => {
            setError('');
            setAdding(true);
            setEditingId(null);
          }}
          disabled={adding}
        >
          <Plus size={14} /> Add resource
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      {adding && (
        <InlineForm
          initial={toForm({})}
          busy={busy}
          onCancel={() => {
            setAdding(false);
            setError('');
          }}
          onSave={add}
        />
      )}

      {resources.length === 0 && !adding ? (
        <p className="muted-text">
          No linked resources yet — add Teams, SharePoint, HubSpot or DevOps
          links.
        </p>
      ) : (
        <>
          <div className="info-groups resource-grid">
            {CATEGORIES.map((cat) => {
              const rows = resources.filter((r) => r.category === cat);
              return (
                <div className="info-group" key={cat}>
                  <div className="info-group-head">
                    <h3>{cat}</h3>
                  </div>
                  {rows.length === 0 ? (
                    <p className="muted-text resource-empty">None yet</p>
                  ) : (
                    rows.map((r) =>
                      editingId === r.id ? (
                        <InlineForm
                          key={r.id}
                          initial={toForm(r)}
                          busy={busy}
                          onCancel={() => {
                            setEditingId(null);
                            setError('');
                          }}
                          onSave={(form) => update(r.id, form)}
                        />
                      ) : (
                        <div className="resource-row" key={r.id}>
                          <span className="resource-marker">
                            <ExternalLink size={13} />
                          </span>
                          <div className="resource-row-main">
                            <b>
                              {isWebUrl(r.url) ? (
                                <a
                                  href={r.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {r.title}
                                </a>
                              ) : (
                                r.title
                              )}
                            </b>
                            <small>
                              {[
                                r.resourceType,
                                r.owner && `Owner: ${r.owner}`,
                                r.lastReviewed && `Reviewed ${r.lastReviewed}`,
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </small>
                            {r.description && <p>{r.description}</p>}
                          </div>
                          {confirmId === r.id ? (
                            <div className="resource-confirm">
                              <button
                                type="button"
                                className="text-button danger-text"
                                disabled={busy}
                                onClick={() => remove(r.id)}
                              >
                                Remove
                              </button>
                              <button
                                type="button"
                                className="text-button"
                                onClick={() => setConfirmId(null)}
                              >
                                Keep
                              </button>
                            </div>
                          ) : (
                            <div className="resource-actions">
                              <button
                                type="button"
                                className={`icon-button key-doc-star${r.keyDocument ? ' is-on' : ''}`}
                                aria-label={
                                  r.keyDocument
                                    ? 'Unpin key document'
                                    : 'Pin as key document'
                                }
                                aria-pressed={r.keyDocument}
                                disabled={busy}
                                onClick={() => toggleKey(r)}
                              >
                                <Star size={14} />
                              </button>
                              <button
                                type="button"
                                className="icon-button"
                                aria-label="Edit"
                                onClick={() => {
                                  setEditingId(r.id);
                                  setAdding(false);
                                  setError('');
                                }}
                              >
                                <Pencil size={14} />
                              </button>
                              <button
                                type="button"
                                className="icon-button"
                                aria-label="Remove"
                                onClick={() => setConfirmId(r.id)}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          )}
                        </div>
                      ),
                    )
                  )}
                </div>
              );
            })}
          </div>

          {keyDocs.length > 0 && (
            <div className="info-group key-docs-panel">
              <div className="info-group-head">
                <h3>Key documents</h3>
              </div>
              <div className="key-docs-links">
                {keyDocs.map((r) =>
                  isWebUrl(r.url) ? (
                    <a
                      key={r.id}
                      href={r.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="key-doc-link"
                    >
                      <ExternalLink size={12} /> {r.title}
                    </a>
                  ) : (
                    <span key={r.id} className="key-doc-link">
                      <ExternalLink size={12} /> {r.title}
                    </span>
                  ),
                )}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function InlineForm({ initial, busy, onCancel, onSave }) {
  const [form, setForm] = useState(initial);
  return (
    <form
      className="inline-form"
      onSubmit={(event) => {
        event.preventDefault();
        onSave(form);
      }}
    >
      <FieldGrid fields={FIELDS} form={form} setForm={setForm} />
      <div className="inline-form-actions">
        <button
          type="button"
          className="text-button"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </button>
        <button type="submit" className="primary small" disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}
